import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ControlConfig } from '../../../models/api/reports/report';
import { ControlConfigurationService } from '../../../services/reports/control-configuration.service';
import {
  ProcessedControl,
  ReportsService,
} from '../../../services/reports/reports.service';
import { ControlValueHandler } from '../../../utilities/control-value-handler';
import { BaseComponent } from '../../base/base.component';

@Component({
  selector: 'app-base-criteria-component',
  template: '',
  standalone: true,
})
export abstract class BaseCriteriaComponent
  extends BaseComponent
  implements OnInit
{
  abstract fg: FormGroup;

  protected abstract CONTROL_CONFIGS: ControlConfig[];
  protected abstract get regionName(): string | null;

  protected reports = inject(ReportsService);

  protected controlConfig = inject(ControlConfigurationService);

  get invalid(): boolean {
    return this.fg?.invalid ?? false;
  }

  ngOnInit(): void {
    this.loadAllConfigurations();
    this.setupFormListeners();
  }

  loadAllConfigurations(): void {
    const report = this.reports.currentReport();
    const controlsMap = report?.reportRequest?.controlsMap;

    if (!controlsMap || controlsMap.size === 0) {
      return;
    }

    const validConfigs = this.CONTROL_CONFIGS.filter((config) => {
      const erfNamesArray = Array.isArray(config.erfNames)
        ? config.erfNames
        : [config.erfNames];
      return erfNamesArray.some((name) => controlsMap.has(name));
    });

    validConfigs.forEach((config) => {
      this.loadControlConfiguration(config);
    });
  }

  /** Override for special handling (e.g., PickLists). */
  protected loadControlConfiguration(config: ControlConfig): void {
    this.controlConfig.loadControlConfiguration(
      config,
      this.fg,
      (cfg, ctrl) => this.storeControlReference(cfg, ctrl),
      this.regionName,
    );
  }

  /** Override to store control references after loading. */
  protected storeControlReference(
    config: ControlConfig,
    control: ProcessedControl,
  ): void {
    // override if needed
  }

  protected ensureFormControl<T = any>(
    name: string,
    initialValue: T | null = null,
  ): FormControl<T | null> {
    let control = this.fg.get(name) as FormControl<T | null>;

    if (!control) {
      control = new FormControl<T | null>(initialValue);
      this.fg.addControl(name, control);
    }

    return control;
  }

  protected setupFormListeners(): void {
    // override if needed
  }

  updateReport(): void {
    this.reports.currentReport.update((report) => {
      if (!report?.reportRequest?.controlsMap) return report;

      this.syncFormToControlsMap(report.reportRequest.controlsMap);
      return report;
    });

    this.updateChildComponents();
  }

  private syncFormToControlsMap(
    controlsMap: Map<string, ProcessedControl>,
  ): void {
    this.CONTROL_CONFIGS.filter((config) => config.visibility()).forEach(
      (config) => {
        const formControl = this.fg.get(config.formControlName);
        if (!formControl || !config.controlRef) return;

        const transformedValue = this.transformValueForSave(
          config,
          formControl.value,
        );
        const formattedValue =
          ControlValueHandler.formatForApi(transformedValue);

        const updatedControl = {
          ...config.controlRef,
          defaultValue: formattedValue,
        };

        if (formattedValue === undefined) {
          delete updatedControl.defaultValue;
        }

        controlsMap.set(config.controlRef.erfName, updatedControl);
      },
    );
  }

  /** Override to transform values before saving. */
  protected transformValueForSave(config: ControlConfig, value: any): any {
    return value;
  }

  /** Override to update nested child components. */
  protected updateChildComponents(): void {
    // override if needed
  }

  /** Reloads form values from controlsMap (e.g., after loading scheduled report). */
  public reloadFormValues(): void {
    this.CONTROL_CONFIGS.forEach((config) => {
      if (!config.visibility()) return;

      const formControl = this.fg.get(config.formControlName);
      if (!formControl) return;

      const erfName = Array.isArray(config.erfNames)
        ? config.erfNames[0]
        : config.erfNames;
      const controlRef = this.controlConfig.getControl(erfName);

      if (controlRef?.defaultValue !== undefined) {
        let value: string | string[] | Date | number = controlRef.defaultValue;

        if (config.isMultiSelect || config.type === 'multiselect') {
          const isCheckboxList =
            controlRef.displayType === 'CHECKBOXLIST' ||
            controlRef.displayType === 'MULTISELECTLISTBOX';
          value = ControlValueHandler.parseMultiSelectValue(
            value,
            isCheckboxList,
          );
        }

        formControl.setValue(value, { emitEvent: false });
      }
    });
  }
}
